import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

const today = () => new Date().toLocaleDateString("en-CA");

export default function StockOutTransfer() {
  const location = useLocation();
  const { user } = useAuth();
  const incoming = useMemo(() => {
    const value = location.state?.inventoryTransferItems;
    return Array.isArray(value) ? value : [];
  }, [location.state?.inventoryTransferItems]);

  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [date, setDate] = useState(today());
  const [sourceKey, setSourceKey] = useState("");
  const [destKey, setDestKey] = useState("");
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const staffName =
    user?.name || user?.full_name || user?.username || user?.email || "Pengguna login";

  const locationKey = (type, id) => `${String(type || "").toLowerCase()}:${id || ""}`;

  const source = locations.find((x) => locationKey(x.type, x.id) === sourceKey);
  const destination = locations.find((x) => locationKey(x.type, x.id) === destKey);

  useEffect(() => {
    let active = true;

    Promise.all([
      api.get("/warehouses"),
      api.get("/outlets"),
      api.get("/products"),
      api.get("/ingredients"),
    ]).then(([wr, or, pr, ir]) => {
      if (!active) return;

      const warehouses = (Array.isArray(wr.data) ? wr.data : []).map((x) => ({
        id: x.id, name: x.name, type: "warehouse",
      }));
      const outlets = (Array.isArray(or.data) ? or.data : []).map((x) => ({
        id: x.id, name: x.name, type: "outlet",
      }));

      setLocations([...warehouses, ...outlets]);
      setProducts(Array.isArray(pr.data) ? pr.data : pr.data?.products || []);
      setIngredients(Array.isArray(ir.data) ? ir.data : ir.data?.ingredients || []);

      if (incoming.length) {
        const first = incoming[0];
        setSourceKey(locationKey(first.location_type, first.location_id));
        setItems(incoming.map((x) => ({
          entity: x.entity,
          entity_id: x.entity_id,
          name: x.name || "",
          qty: String(x.qty ?? 1),
          unit: x.unit || "pcs",
        })));
      }
    }).catch((err) => {
      if (active) setError(
        err.response?.data?.detail || "Gagal memuat lokasi dan daftar barang."
      );
    }).finally(() => {
      if (active) setLoading(false);
    });

    return () => { active = false; };
  }, [incoming]);

  const availableEntities = useMemo(
    () => [...ingredients.map((x) => ({ ...x, entity: "ingredient" })),
      ...products.map((x) => ({ ...x, entity: "product" }))],
    [ingredients, products]
  );

  function addItem() {
    setItems((old) => [...old, {
      entity: "ingredient", entity_id: "", name: "", qty: "1", unit: "pcs",
    }]);
  }

  function updateItem(index, patch) {
    setItems((old) => old.map((item, i) => i === index ? { ...item, ...patch } : item));
  }

  function chooseEntity(index, value) {
    const entity = availableEntities.find(
      (x) => `${x.entity}:${x.id}` === value
    );
    if (!entity) {
      updateItem(index, { entity_id: "", name: "", unit: "pcs" });
      return;
    }
    updateItem(index, {
      entity: entity.entity,
      entity_id: entity.id,
      name: entity.name || "",
      unit: entity.unit || "pcs",
    });
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    setResult(null);

    if (!date || !source || !destination) {
      setError("Lengkapi tanggal, lokasi asal, dan lokasi tujuan.");
      return;
    }
    if (sourceKey === destKey) {
      setError("Lokasi asal dan tujuan tidak boleh sama.");
      return;
    }
    if (!items.length || items.some((x) =>
      !x.entity_id || !(Number(x.qty) > 0) || !Number.isFinite(Number(x.qty))
    )) {
      setError("Pastikan semua barang dipilih dan jumlahnya lebih dari nol.");
      return;
    }

    const seen = new Set();
    for (const item of items) {
      const key = `${item.entity}:${item.entity_id}`;
      if (seen.has(key)) {
        setError("Barang yang sama tidak boleh muncul lebih dari satu baris.");
        return;
      }
      seen.add(key);
    }

    setSaving(true);
    try {
      const response = await api.post("/transfers", {
        source_type: source.type,
        source_id: source.id,
        dest_type: destination.type,
        dest_id: destination.id,
        items: items.map((x) => ({
          entity: x.entity,
          entity_id: x.entity_id,
          name: x.name,
          qty: Number(x.qty),
        })),
        notes: [
          `Tanggal pengiriman: ${date}`,
          `Petugas pengajuan: ${staffName}`,
          notes.trim(),
        ].filter(Boolean).join("\n"),
      });

      setResult(response.data);
      setItems([]);
      setNotes("");
    } catch (err) {
      setError(err.response?.data?.detail || "Gagal membuat pengajuan transfer.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="rounded-lg border bg-white p-5">Memuat data lokasi dan barang…</div>;

  return (
    <form onSubmit={submit} className="space-y-5 rounded-lg border bg-white p-5">
      <div>
        <h2 className="text-lg font-semibold">Formulir Pengiriman Transfer</h2>
        <p className="mt-1 text-sm text-slate-600">
          Data dari Inventory akan terisi otomatis. Pengajuan belum mengurangi stok;
          stok sumber berkurang saat transfer disetujui dan dikirim.
        </p>
      </div>

      {result ? (
        <div className="space-y-3 rounded-md border p-4">
          <p className="font-semibold">Pengajuan transfer berhasil dibuat.</p>
          <p>Nomor transfer: <strong>{result.transfer_no || result.id}</strong></p>
          <p>Status: <strong>{result.status || "REQUESTED"}</strong></p>
          <p className="text-sm text-slate-600">
            Lanjutkan proses persetujuan dan dispatch sesuai kewenangan yang berlaku.
          </p>
          <button type="button" className="rounded-md border px-4 py-2"
            onClick={() => setResult(null)}>
            Buat transfer berikutnya
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="space-y-1 text-sm">
              Tanggal pengiriman
              <input type="date" className="w-full rounded-md border p-2"
                value={date} onChange={(e) => setDate(e.target.value)} required />
            </label>
            <label className="space-y-1 text-sm">
              Petugas
              <input className="w-full rounded-md border bg-slate-50 p-2"
                value={staffName} readOnly />
            </label>
            <label className="space-y-1 text-sm">
              Lokasi asal
              <select className="w-full rounded-md border p-2" value={sourceKey}
                onChange={(e) => setSourceKey(e.target.value)} required>
                <option value="">Pilih lokasi asal</option>
                {locations.map((x) => (
                  <option key={locationKey(x.type, x.id)} value={locationKey(x.type, x.id)}>
                    {x.name} ({x.type === "warehouse" ? "Gudang" : "Outlet"})
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1 text-sm">
              Lokasi tujuan
              <select className="w-full rounded-md border p-2" value={destKey}
                onChange={(e) => setDestKey(e.target.value)} required>
                <option value="">Pilih lokasi tujuan</option>
                {locations.filter((x) => locationKey(x.type, x.id) !== sourceKey)
                  .map((x) => (
                    <option key={locationKey(x.type, x.id)} value={locationKey(x.type, x.id)}>
                      {x.name} ({x.type === "warehouse" ? "Gudang" : "Outlet"})
                    </option>
                  ))}
              </select>
            </label>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-semibold">Daftar barang yang dikirim</h3>
              <button type="button" onClick={addItem}
                className="rounded-md border px-3 py-2 text-sm">
                + Tambah barang
              </button>
            </div>

            {items.length === 0 && (
              <p className="rounded-md border border-dashed p-4 text-sm text-slate-500">
                Keranjang kosong. Tambahkan barang untuk membuat transfer.
              </p>
            )}

            {items.map((item, index) => (
              <div key={`${item.entity_id || "new"}-${index}`}
                className="grid grid-cols-1 gap-3 rounded-md border p-3 md:grid-cols-[2fr_1fr_auto]">
                <label className="space-y-1 text-sm">
                  Barang
                  <select className="w-full rounded-md border p-2"
                    value={item.entity_id ? `${item.entity}:${item.entity_id}` : ""}
                    onChange={(e) => chooseEntity(index, e.target.value)} required>
                    <option value="">Pilih barang</option>
                    {availableEntities.map((x) => (
                      <option key={`${x.entity}:${x.id}`} value={`${x.entity}:${x.id}`}>
                        {x.name} — {x.entity === "ingredient" ? "Bahan baku" : "Produk"}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1 text-sm">
                  Jumlah kirim ({item.unit || "pcs"})
                  <input type="number" min="0.001" step="any"
                    className="w-full rounded-md border p-2" value={item.qty}
                    onChange={(e) => updateItem(index, { qty: e.target.value })} required />
                </label>
                <div className="flex items-end">
                  <button type="button" className="rounded-md border px-3 py-2 text-sm"
                    onClick={() => setItems((old) => old.filter((_, i) => i !== index))}>
                    Hapus
                  </button>
                </div>
              </div>
            ))}
          </div>

          <label className="block space-y-1 text-sm">
            Catatan pengiriman
            <textarea className="w-full rounded-md border p-2" rows={3}
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Catatan, nomor dokumen internal, atau keterangan pengiriman" />
          </label>

          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

          <button type="submit" disabled={saving || !items.length}
            className="rounded-md bg-black px-4 py-2 text-white disabled:opacity-50">
            {saving ? "Menyimpan…" : "Ajukan Transfer"}
          </button>
        </>
      )}
    </form>
  );
}
